import { Boundaries } from "@planzone/core";
import { gantt, Identifiable, List } from "@planzone/dto";
import { Event, events } from "@planzone/events";
import { setRequestStateToPending } from "../../utils/setRequestStateToPending";
import { setRequestStateToSuccess } from "../../utils/setRequestStateToSuccess";
import { setRequestStateToFailure } from "../../utils/setRequestStateToFailure";
import { createStateReducer } from "../../utils/createStateReducer";
import { resetState } from "../../utils/resetState";
import { calculateAdvancement } from "../../utils/calculateAdvancement";
import { reduceGantt } from "../../common/gantt";
import { State, Selector } from "./types";
import { handleRetrieveProjectGantt } from "./retrieveProjectGantt";
import { createSelector } from "./selector";

const initialState: State = [];

function calculateTasksAdvancement(
  list: Identifiable<List>,
): Identifiable<List> {
  return {
    ...list,
    tasks: list.tasks.map(calculateAdvancement),
    subLists: list.subLists.map(calculateTasksAdvancement),
  };
}

function handleRequest(
  state: State,
  zone: Boundaries["retrieveZoneGantt"]["request"],
): State {
  return [...state, { request: setRequestStateToPending(), zone, gantt }];
}

function handleSuccess(
  state: State,
  { zone, gantt }: Boundaries["retrieveZoneGantt"]["success"],
): State {
  return state.map((res) =>
    res.zone.uuid === zone.uuid
      ? {
          ...res,
          request: setRequestStateToSuccess(),
          zone,
          gantt: {
            ...gantt,
            projects: gantt.projects.map((project) => ({
              ...project,
              lists: project.lists.map(calculateTasksAdvancement),
            })),
          },
        }
      : res,
  );
}

function handleFailure(
  state: State,
  zone: Boundaries["retrieveZoneGantt"]["request"],
): State {
  return state.map((res) =>
    res.zone.uuid === zone.uuid
      ? { ...res, request: setRequestStateToFailure() }
      : res,
  );
}

function propagateEvent(state = initialState, event: Event): State {
  return state.map((req) => ({
    ...req,
    gantt: reduceGantt(req.gantt, event),
  }));
}

const reduceState = resetState(
  initialState,
  createStateReducer(
    initialState,
    {
      [events.retrieveZoneGanttRequested]: handleRequest,
      [events.retrieveZoneGanttSucceeded]: handleSuccess,
      [events.retrieveZoneGanttFailed]: handleFailure,
      [events.retrieveProjectGanttSucceeded]: handleRetrieveProjectGantt,
    },
    propagateEvent,
  ),
  [events.changePlanzoneSucceeded],
);

export type { State, Selector };

export { reduceState, initialState, createSelector };
